
import React from 'react';
import { motion } from 'framer-motion';
import { House, Navigation, ArrowRight, Zap, Trophy, History, Clock } from 'lucide-react';
import { Driver, Order, SystemConfig } from '../../../types';
import { FutureMissionCard } from './FutureMissionCard';

interface Props {
  driver: Driver;
  futureOrders: Order[];
  config: SystemConfig;
  onLogout: () => void;
  isAdmin?: boolean;
  onBackToDispatcher?: () => void;
  // v60.3: Entregas do dia calculadas corretamente
  todaysDeliveries?: number;
}

export const ReturnToHubState: React.FC<Props> = ({ driver, futureOrders, config, onLogout, isAdmin, onBackToDispatcher, todaysDeliveries }) => {
  const handleReturnNavigation = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${config.businessLat},${config.businessLng}`;
    window.open(url, '_blank');
  };

  const hasFuture = futureOrders.length > 0;

  return (
    <div className="min-h-[80vh] flex flex-col items-center p-6 animate-in fade-in duration-700">
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-5 pointer-events-none"></div>

      {/* Header de Sucesso */}
      <div className="w-full max-w-sm text-center mb-8 mt-4">
        <div className="w-20 h-20 bg-status-ready/10 border-2 border-status-ready/30 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 shadow-xl">
          <Trophy className="w-10 h-10 text-status-ready" />
        </div>
        <h2 className="text-3xl font-black text-text-primary uppercase tracking-tighter mb-2">Entregas Concluídas!</h2>
        <p className="text-text-muted text-sm font-bold uppercase tracking-widest leading-relaxed px-4">
          Você limpou sua rota. Bom trabalho, {driver.name.split(' ')[0]}!
        </p>
      </div>

      {/* Seção de Próximos Pedidos (Sofia) */}
      {hasFuture ? (
        <div className="w-full max-w-md space-y-4 mb-10">
          <div className="flex items-center gap-3 px-2">
            <div className="w-2 h-2 rounded-full bg-sal-cyber-teal animate-ping"></div>
            <h3 className="text-xs font-black text-sal-cyber-teal uppercase tracking-[0.3em]">Radar de Próxima Rota</h3>
          </div>
          {futureOrders.map(o => (
            <FutureMissionCard key={o.id} order={o} />
          ))}
          <div className="bg-sal-cyber-teal/5 p-4 rounded-2xl border border-sal-cyber-teal/10 flex items-start gap-3">
            <Zap className="w-5 h-5 text-sal-cyber-teal shrink-0" />
            <p className="text-xs text-text-muted font-medium leading-tight">
              <strong className="text-text-primary">Sofia:</strong> Já estou organizando sua próxima viagem. Volte logo para o Hub para ganhar tempo!
            </p>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-sm bg-card-bg/60 backdrop-blur-xl rounded-[2rem] p-8 border border-border-dim text-center mb-10 shadow-xl">
          <div className="flex justify-center gap-1 mb-4">
            {[1, 2, 3].map(i => <div key={i} className="w-1 h-4 bg-border-strong rounded-full animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />)}
          </div>
          <p className="text-sm text-text-muted font-bold uppercase tracking-widest">Aguardando novos pedidos...</p>
        </div>
      )}

      {/* Botão de Retorno Tático */}
      <div className="w-full max-w-sm space-y-4">
        <button
          onClick={handleReturnNavigation}
          className="w-full py-6 bg-sal-cyber-teal text-badge-text-inv font-black rounded-[2rem] shadow-[0_15px_40px_rgba(6,182,212,0.3)] hover:brightness-110 active:scale-95 transition-all flex flex-col items-center justify-center relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
          <div className="relative z-10 flex items-center gap-3 mb-1">
            <House className="w-6 h-6" />
            <span className="text-xl uppercase tracking-[0.1em]">Voltar para a Loja</span>
          </div>
          <span className="relative z-10 text-xs font-black opacity-60 uppercase tracking-widest">Iniciar navegação GPS</span>
        </button>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-card-bg/50 p-4 rounded-3xl border border-border-dim flex flex-col items-center shadow-sm">
            <History className="w-4 h-4 text-icon-muted mb-2" />
            <span className="text-xs font-black text-text-primary uppercase">{todaysDeliveries || 0} Entregas</span>
            <span className="text-xs text-text-muted font-bold uppercase mt-1">Hoje</span>
          </div>
          <div className="bg-card-bg/50 p-4 rounded-3xl border border-border-dim flex flex-col items-center shadow-sm">
            <Clock className="w-4 h-4 text-icon-muted mb-2" />
            <span className="text-xs font-black text-text-primary uppercase">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <span className="text-xs text-text-muted font-bold uppercase mt-1">Sessão Ativa</span>
          </div>
        </div>

        {isAdmin && (
          <div className="space-y-3 w-full">
            {onBackToDispatcher && (
              <button
                onClick={onBackToDispatcher}
                className="w-full py-4 text-status-info text-xs font-black uppercase tracking-[0.3em] hover:text-status-info/80 transition-colors border border-status-info/20 rounded-2xl mb-2"
              >
                Retornar ao Painel Admin
              </button>
            )}
            <button
              onClick={onLogout}
              className="w-full py-4 text-text-muted text-xs font-black uppercase tracking-[0.3em] hover:text-text-primary transition-colors"
            >
              Desconectar Piloto (DEBUG)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
